import { Component, inject } from '@angular/core';
import { VehicleService } from 'orchestrator';
import { ListService } from '@abp/ng.core';
import {
  ConfirmationService,
} from '@abp/ng.theme.shared';
import {
  VehicleCreateDto,
  VehicleDto,
  VehicleExcelDownloadDto,
  VehicleUpdateDto,
} from 'orchestrator';
import { VEHICLE_CONFIG, VEHICLE_FIELDS } from './vehicle.config';
import { BaseHierarchicalCrudComponent, GenericForm, ZnHierarchicalViewComponent } from '@zendra/shared';
import { CommonModule } from '@angular/common';
import { FieldMapperHelper } from '@zendra/shared';

@Component({
  selector: 'app-vehicle',
  imports: [
    CommonModule,
    GenericForm,
    ZnHierarchicalViewComponent,
  ],
  providers: [ListService, VehicleService, ConfirmationService],
  templateUrl: './vehicle.html',
  styleUrl: './vehicle.scss',
})
export class Vehicle extends BaseHierarchicalCrudComponent<
  VehicleDto,
  VehicleCreateDto,
  VehicleUpdateDto,
  VehicleExcelDownloadDto
> {
  service = inject(VehicleService);
  config = VEHICLE_CONFIG;
  fields = VEHICLE_FIELDS;

  private gridHookIntervalId: any;
  private gridHooked = false;

  constructor() {
    super();

    const fkDef = this.fields.find(f => f.key === 'vehicleTypeId') as any;
    if (fkDef?.column) {
      fkDef.column = {
        ...fkDef.column,
        editable: false,
        editor: undefined,
        cssClass: ((fkDef.column.cssClass ? fkDef.column.cssClass + ' ' : '') + 'read-only'),
      };

      fkDef.column.cssClass = (fkDef.column.cssClass ? fkDef.column.cssClass + ' ' : '') + 'read-only';
    }
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    this.gridHookIntervalId = setInterval(() => {
      if (this.gridHooked) {
        clearInterval(this.gridHookIntervalId);
        return;
      }

      const grid = (this as any).crudPage?.gridTable?.grid;
      if (!grid) {
        return;
      }

      const slick = grid.slickGrid;
      if (!slick?.onBeforeEditCell) {
        clearInterval(this.gridHookIntervalId);
        return;
      }

      slick.onBeforeEditCell.subscribe((_e: any, args: any) => {
        const field = args?.column?.field ?? args?.column?.id;

        if (field === 'vehicleTypeId') {
          return false;
        }
        return true;
      });

      this.gridHooked = true;
      clearInterval(this.gridHookIntervalId);
    }, 250);
  }

  override ngOnDestroy(): void {
    if (this.gridHookIntervalId) {
      clearInterval(this.gridHookIntervalId);
    }
    super.ngOnDestroy();
  }

  override edit(row: VehicleDto): void {
    this.selectedEntity = { ...row };

    const fields = FieldMapperHelper.mapFieldsForForm(this.fields, row, this.injector).map(f => ({
      ...f,
      required: f.required,
      validators: f.validators
    }));

    const vehicleTypeField = fields.find(f => (f as any).key === 'vehicleTypeId') as any;
    if (vehicleTypeField) {
      vehicleTypeField['disabled'] = false;
      vehicleTypeField['readOnly'] = false;

      if (vehicleTypeField['fieldOptions']) {
        vehicleTypeField['fieldOptions'] = {
          ...vehicleTypeField['fieldOptions'],
          disabled: false,
          readOnly: false,
        };
      }

      if (vehicleTypeField['templateOptions']) {
        vehicleTypeField['templateOptions'] = {
          ...vehicleTypeField['templateOptions'],
          disabled: false,
          readonly: false,
        };
      }

      if (vehicleTypeField['formControl']?.disable) {
        vehicleTypeField['formControl'].disable({ emitEvent: false });
      }
    }

    this.genericForm.open({
      title: `${this.localizationResource.LanguageService}::EditRecord`,
      fields,
      entityLocaleKey: this.config.localizationKey
    });
  }

  protected prepareUpdateDto(data: any, selected: VehicleDto): VehicleUpdateDto {
    return {
      name: data.name,
      code: data.code,
      isActive: data.isActive,
      deactivationDate: data.deactivationDate,
      sapCode: data.sapCode,
      vendorId: data.vendorId,
      internalOrderId: data.internalOrderId,
      notes: data.notes,
      interfaceStatus: data.interfaceStatus,
      plate: data.plate,
      hasGps: data.hasGps,
      horsePower: data.horsePower,
      co2Emisions: data.co2Emisions,
      deactivationReasonId: data.deactivationReasonId,
      energySourceId: data.energySourceId,
      truckCategoryId: data.truckCategoryId,
      vehicleTechnologyId: data.vehicleTechnologyId,
      brandId: data.brandId,
      truckLogicId: data.truckLogicId,
      tractionTypeId: data.tractionTypeId,
      modelId: data.modelId,
      concurrencyStamp: selected.concurrencyStamp,
      vehicleTypeId: data.vehicleTypeId
    };
  }

  protected getEntityName(): string {
    return 'Vehicle';
  }
}
